import { useCallback, useMemo, useState } from 'react';
import { useAsync } from 'react-use';
import { useDispatch, useSelector } from 'react-redux';
import { BusinessSelectionChangedRedirect } from 'routes/auth';
import { useTranslation } from 'react-i18next';

import AuthenticationService from 'services/AuthenticationService';
import { serviceChooseBusinessStart } from 'store/authentication/authenticationAction';
import Select from 'components/Inputs/Select';
import Loader from 'components/Kizen/Loader';
import AuthForm from 'components/Kizen/AuthForm';
import Button from 'components/Button';
import { KizenTypography } from 'app/typography';
import { breakpoints, gutters } from 'app/spacing';
import { useSetTitleOnLoad } from 'hooks/useSetTitleOnLoad';
import { serviceLogoutStart } from 'store/authentication/authenticationAction';
import {
  DATA_MANAGER_SESSION_ACTIONS,
  useDataManagerSession,
} from 'app/dataManagerSession';

import { postChangeBusinessMessage } from 'store/authentication/channels';
import {
  selectedBusinessLocalStorage,
  isExternalUrl,
  oauthSessionStorage,
} from 'storage/auth';

const styles = `
  .Authform-wrapper {
    width: 80%;
    @media (min-width: ${breakpoints.md}px) {
      width: 400px;
    }
  }

  .ChooseBusiness-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .ChooseBusiness-select {
    margin-top: ${gutters.large};
    width: 100%;
    margin-bottom: ${gutters.large};
  }

  .ChooseBusiness-select > label > span {
    margin-bottom: ${gutters.standard};
  }

  .ChooseBusiness-button {
    margin: 0;
    margin-left: auto;
  }
`;

const businessLabel = (business) => {
  const { name, companyName, licenseName } = business;

  let label = name;

  if (companyName && name !== companyName) {
    if (licenseName) {
      label = `${name} - ${companyName} (${licenseName})`;
    } else {
      label = `${name} - ${companyName}`;
    }
  } else {
    if (licenseName) {
      label = `${name} (${licenseName})`;
    }
  }

  return label;
};

const mapBuisnessesToSelectOptions = (business) => {
  const { id, isValidForSession = true } = business;

  return {
    value: id,
    label: businessLabel(business),
    isValidForSession,
  };
};

const ChooseBusinessPage = ({ title, location, history }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  useSetTitleOnLoad(title(t));
  const instanceId = useSelector((s) => s.authentication.instanceId);
  const isLoadingBusiness = useSelector(
    (s) => s.authentication.isLoadingBusiness
  );

  const [selectedOption, setSelectedOption] = useState(null);
  const [fetchError, setFetchError] = useState('');
  const [formError, setFormError] = useState('');
  const [newChosenBusiness, setNewChosenBusiness] = useState();
  const { dispatchSessionState } = useDataManagerSession();

  const { value: businesses, loading } = useAsync(async () => {
    try {
      const currentUrl = new URL(window.location);
      const showAllBusinesses = currentUrl.searchParams.get('all');
      const response =
        await AuthenticationService.fetchBusinesses(showAllBusinesses);

      if (!response || response.length === 0) {
        setFetchError(t('Sorry, unable to retrieve your businesses'));
        return;
      }

      return response;
    } catch (error) {
      setFetchError(error.message);
    }
  }, [t]);

  const handleChangeBusiness = useCallback(
    async (businessId) => {
      setFormError('');
      const [error, chosenBusiness] = await dispatch(
        serviceChooseBusinessStart(businessId)
      );

      dispatchSessionState({
        type: DATA_MANAGER_SESSION_ACTIONS.DELETE,
      });

      if (!error) {
        const hasRedirect = oauthSessionStorage.val;
        if (hasRedirect) {
          // we are loged in, with a valid business and have valid redirect
          oauthSessionStorage.clear();
          if (isExternalUrl(hasRedirect)) {
            // absolute url on the api host: react-router cannot handle it, and
            // rendering the in-app redirect would race the full navigation
            postChangeBusinessMessage(instanceId);
            selectedBusinessLocalStorage.val = businessId;
            window.location.href = hasRedirect;
            return;
          }
          history.replace(hasRedirect);
        }
        postChangeBusinessMessage(instanceId);
        selectedBusinessLocalStorage.val = businessId;
        setNewChosenBusiness(chosenBusiness);
      } else {
        setFormError(error.message);
      }
    },
    [dispatch, dispatchSessionState, history, instanceId]
  );

  const handleSelectBusiness = useCallback(
    (option) => {
      if (option?.isValidForSession) {
        setSelectedOption(option);
      } else {
        dispatch(serviceLogoutStart());
        history.replace('/login');
      }
    },
    [setSelectedOption, dispatch, history]
  );

  const { businessesWithinSession, options, priorBusiness } = useMemo(() => {
    if (!businesses) {
      return {
        businessesWithinSession: [],
        options: [],
        priorBusiness: null,
      };
    }

    const businessesWithinSession = businesses.filter(
      ({ isValidForSession }) => isValidForSession
    );

    const businessesOutsideSession = businesses.filter(
      ({ isValidForSession }) => !isValidForSession
    );

    const priorBusiness =
      businessesWithinSession.find(
        ({ id }) => id === selectedBusinessLocalStorage.val
      ) ?? null;

    const options = [
      {
        label: t('Available Businesses'),
        options: businessesWithinSession.map(mapBuisnessesToSelectOptions),
      },
      {
        label: t('Require Different Auth'),
        options: businessesOutsideSession.map(mapBuisnessesToSelectOptions),
      },
    ].filter(({ options }) => options.length > 0);

    return {
      businessesWithinSession,
      options,
      priorBusiness,
    };
  }, [t, businesses]);

  const params = new URLSearchParams(location.search);
  const toggle = params.get('toggle')?.toLowerCase() === 'true';

  const shouldToggleBusiness =
    toggle && businessesWithinSession.length === 2 && Boolean(priorBusiness);

  if (shouldToggleBusiness && !isLoadingBusiness) {
    const otherBusiness = businessesWithinSession.find(
      (business) => business.id !== priorBusiness.id
    );
    handleChangeBusiness(otherBusiness.id);
  } else if (priorBusiness && !selectedOption) {
    setSelectedOption({
      value: priorBusiness.id,
      label: businessLabel(priorBusiness),
      isValidForSession: priorBusiness.isValidForSession,
    });
  }

  if (newChosenBusiness) {
    return <BusinessSelectionChangedRedirect />;
  }

  if (loading || shouldToggleBusiness) {
    return <Loader loading />;
  }

  return (
    <AuthForm styles={styles}>
      <div className="Authform-wrapper">
        <Loader loading={loading}>
          <form action="">
            <KizenTypography type="subheader" weight="bold">
              {t('Choose Business')}
            </KizenTypography>

            <Select
              value={selectedOption}
              label={t('Choose Business')}
              className="ChooseBusiness-select"
              options={options}
              placeholder={t('Choose business')}
              styles={{
                container: (provided) => ({
                  ...provided,
                  width: '100%',
                }),
              }}
              onChange={handleSelectBusiness}
            />
            {!!(businesses && businesses.length) && (
              <div className="ChooseBusiness-actions">
                <Button
                  className="ChooseBusiness-button"
                  onClick={(ev) => {
                    ev.preventDefault();
                    handleChangeBusiness(selectedOption.value);
                  }}
                  disabled={!selectedOption || isLoadingBusiness}
                >
                  {t('Submit')}
                </Button>
                {formError && (
                  <KizenTypography className="Authform-error">
                    {formError}
                  </KizenTypography>
                )}
              </div>
            )}
            {fetchError && (
              <KizenTypography className="Authform-error">
                {fetchError}
              </KizenTypography>
            )}
          </form>
        </Loader>
      </div>
    </AuthForm>
  );
};

export default ChooseBusinessPage;
